import React from 'react';
import { Button, Modal } from 'antd';
import {
  DatabaseOutlined,
  EditOutlined,
  FilterOutlined,
  PlusCircleOutlined,
  QuestionCircleOutlined,
  SearchOutlined,
  TeamOutlined,
  ToolOutlined,
} from '@ant-design/icons';
import { TEAM_ASSISTANT_OVERLAY_Z_INDEX } from '../../utils/teamAssistantZIndex';
import './team-assistant-help-modal.css';

const ExamplePrompt = ({ children }) => (
  <code className="team-assistant-help-example">{children}</code>
);

const HelpSection = ({ icon, title, children }) => (
  <section className="team-assistant-help-section">
    <div className="team-assistant-help-section-head">
      <span className="team-assistant-help-section-icon" aria-hidden>
        {icon}
      </span>
      <h3 className="team-assistant-help-section-title">{title}</h3>
    </div>
    <div className="team-assistant-help-section-body">{children}</div>
  </section>
);

const TeamAssistantHelpModal = ({ open, onClose }) => (
  <Modal
    open={open}
    onCancel={onClose}
    footer={null}
    width={680}
    centered
    zIndex={TEAM_ASSISTANT_OVERLAY_Z_INDEX}
    destroyOnClose
    className="team-assistant-help-modal"
    title={
      <div className="team-assistant-help-modal-head">
        <QuestionCircleOutlined className="team-assistant-help-modal-head-icon" aria-hidden />
        <div>
          <div className="team-assistant-help-modal-head-title">Team Assistant guide</div>
          <div className="team-assistant-help-modal-head-sub">What you can do in this workspace</div>
        </div>
      </div>
    }
  >
    <div className="team-assistant-help-modal-body">
      <div className="team-assistant-help-lead">
        <TeamOutlined className="team-assistant-help-lead-icon" aria-hidden />
        <p>
          One shared conversation for admin and staff. Replies use team uploads, Market Finder data, and
          optional web search — not a personal ChatGPT account.
        </p>
      </div>

      <HelpSection icon={<SearchOutlined />} title="Carriers &amp; Market Finder">
        <p className="team-assistant-help-text">Try prompts like:</p>
        <ul className="team-assistant-help-examples">
          <li>
            <ExamplePrompt>Find XYZ Insurance and summarize their LOBs and states.</ExamplePrompt>
          </li>
          <li>
            <ExamplePrompt>What are the limits for [company name]?</ExamplePrompt>
          </li>
        </ul>
        <p className="team-assistant-help-text team-assistant-help-text--muted">
          The assistant searches the company catalog and can load full profiles from the database.
        </p>
      </HelpSection>

      <HelpSection icon={<DatabaseOutlined />} title="Knowledge base">
        <p className="team-assistant-help-text">
          Open the <strong>database</strong> icon in the chat header (full-screen view recommended).
        </p>
        <ul className="team-assistant-help-feature-list">
          <li>
            <strong>Overview</strong> — Upload health, search index, catalog, and memory counts.
          </li>
          <li>
            <strong>Files</strong> — Team PDFs and Office docs; see which are indexed for search.
          </li>
          <li>
            <strong>Deep search</strong> — Past questions and answers by carrier, state, LOB, and more.
          </li>
          <li>
            <strong>Team memory</strong> — Confirmed facts in every reply. Staff propose via the bookmark on
            a message; admins confirm.
          </li>
        </ul>
      </HelpSection>

      <HelpSection icon={<ToolOutlined />} title="Composer &amp; documents">
        <ul className="team-assistant-help-feature-list">
          <li>
            Attach files in the message bar or manage uploads in the knowledge base. Indexed files are
            searchable for everyone.
          </li>
          <li>
            Example:{' '}
            <ExamplePrompt>Summarize the attached underwriting guide.</ExamplePrompt>
          </li>
          <li>
            Toggle <strong>Web search</strong> for current external facts. Voice input works in Chrome and
            Edge.
          </li>
        </ul>
      </HelpSection>

      <HelpSection icon={<EditOutlined />} title="Update company descriptions">
        <p className="team-assistant-help-text">
          The assistant can <strong>draft</strong> General info text; it does not save to the catalog
          automatically.
        </p>
        <ol className="team-assistant-help-steps">
          <li>
            Ask:{' '}
            <ExamplePrompt>
              Write an updated general info description for XYZ Insurance based on …
            </ExamplePrompt>
          </li>
          <li>
            Copy the reply → <strong>Companies</strong> → Edit → paste into <strong>General info</strong> →
            Save.
          </li>
        </ol>
      </HelpSection>

      <HelpSection icon={<PlusCircleOutlined />} title="Admins: add a company">
        <p className="team-assistant-help-text">
          Ask for a new carrier with description and lines of business. A confirm card appears in chat —
          approve to create the record, then edit if needed.
        </p>
      </HelpSection>

      <HelpSection icon={<FilterOutlined />} title="Browse &amp; filters">
        <p className="team-assistant-help-text">
          Use the left sidebar to filter by teammate or date. Filters change what you see only; the team
          history is still one shared thread.
        </p>
      </HelpSection>
    </div>

    <footer className="team-assistant-help-modal-footer">
      <Button type="primary" size="large" onClick={onClose} className="team-assistant-help-modal-done">
        Got it
      </Button>
    </footer>
  </Modal>
);

export default TeamAssistantHelpModal;
